import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ApiError, resources, setUnauthenticatedHandler, type Me } from '@/lib/api';

/**
 * Session state.
 *
 * `status` is a three-state machine rather than a boolean, because "not signed in" and
 * "we have not checked yet" must render differently. Conflating them is what causes a
 * signed-in user to see the sign-in page flash on every reload.
 */
export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';

export const useSession = defineStore('session', () => {
  const status = ref<SessionStatus>('unknown');
  const me = ref<Me | null>(null);
  const loading = ref(false);

  const organizationId = computed(() => me.value?.memberships[0]?.organizationId ?? null);
  const displayName = computed(
    () => me.value?.user.display_name ?? me.value?.user.email?.split('@')[0] ?? 'there',
  );
  const onboardingComplete = computed(() => me.value?.onboarding.complete ?? false);
  const suspended = computed(() => me.value?.account.suspended ?? false);

  /**
   * Whether this user has dismissed onboarding in this browser.
   *
   * Onboarding counts as complete only once the account has a key AND a pod, which
   * someone exploring the console may never want. Without a way to say "not now" the
   * router's nudge toward /welcome became a trap: every route to the dashboard bounced
   * straight back, so Skip and "Go to dashboard" appeared to do nothing. Kept per user
   * in localStorage because it is a convenience, not account state; storage that
   * throws (private mode, blocked site data) just means the nudge comes back.
   */
  const onboardingDismissed = ref(false);
  const dismissalKey = () =>
    me.value?.user.id ? `servepod.onboarding.dismissed.${me.value.user.id}` : null;

  function loadDismissal(): void {
    const key = dismissalKey();
    try {
      onboardingDismissed.value = key !== null && localStorage.getItem(key) === '1';
    } catch {
      onboardingDismissed.value = false;
    }
  }

  function dismissOnboarding(): void {
    onboardingDismissed.value = true;
    const key = dismissalKey();
    try {
      if (key !== null) localStorage.setItem(key, '1');
    } catch {
      /* The in-memory flag still holds for this visit. */
    }
  }

  /** Where a signed-in user with no other destination should land. */
  const needsOnboarding = computed(() => !onboardingComplete.value && !onboardingDismissed.value);

  /**
   * In-flight refresh, shared by concurrent callers.
   *
   * The router guard and the root component both resolve the session on first load.
   * Without this, a slow response means both fire and the app makes two identical
   * requests before it has rendered anything.
   */
  let inFlight: Promise<void> | null = null;

  async function refresh(): Promise<void> {
    if (inFlight !== null) return inFlight;
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  async function doRefresh(): Promise<void> {
    loading.value = true;
    try {
      me.value = await resources.me();
      status.value = 'authenticated';
      loadDismissal();
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        me.value = null;
        status.value = 'anonymous';
      }
      // Any other failure (network, 500) leaves the status untouched: signing a user
      // out because the API blipped would lose their work for no reason.
    } finally {
      loading.value = false;
    }
  }

  async function signIn(email: string, password: string): Promise<void> {
    await resources.login({ email, password });
    await refresh();
  }

  async function signUp(input: {
    email: string;
    password: string;
    displayName?: string;
    organizationName?: string;
  }): Promise<void> {
    await resources.register(input);
    await refresh();
  }

  async function signOut(): Promise<void> {
    try {
      await resources.logout();
    } finally {
      me.value = null;
      status.value = 'anonymous';
    }
  }

  // A 401 from any request means the session is gone, whatever this store believes.
  setUnauthenticatedHandler(() => {
    me.value = null;
    status.value = 'anonymous';
  });

  return {
    status,
    me,
    loading,
    organizationId,
    displayName,
    onboardingComplete,
    onboardingDismissed,
    needsOnboarding,
    dismissOnboarding,
    suspended,
    refresh,
    signIn,
    signUp,
    signOut,
  };
});
